import { ArrowRightAltRounded, FeedbackRounded, Timer } from '@material-ui/icons';
import { Huddle, ProposalStatus, Role } from 'lib/generated/graphql';
import { DateTime } from 'luxon';
import moment from 'moment';
import Image from 'next/image';
import Link from 'next/link';
import { useRef } from 'react';
import Countdown from 'react-countdown';
import InfoItem from 'src/screens/admin/screen-components/InfoItem';
import useAuth from 'src/shared-components/Auth';
import Avatar from 'src/shared-components/Avatar';
import CompanyInfo from 'src/shared-components/HuddleCard/shared/CompanyInfo';
import Tag from 'src/shared-components/Tags/Tag';
import { PayNowButton, ReviewHuddleAgreementButton } from '../../Huddle/components/Expansion';

const DEFAULT_USER_AVATAR = process.env.DEFAULT_USER_AVATAR || '';

const HuddleStatusHeader = ({ huddle }: { huddle: Huddle }) => {
  const renderer = dateObj => {
    if (dateObj.completed) {
      return <span>Reservation Period has expired</span>;
    } else {
      return (
        <span>
          {parseInt(dateObj.days)} days and {parseInt(dateObj.hours)} hours before your reservation expires
        </span>
      );
    }
  };

  const teamModalRef = useRef<any>();
  const openTeamModal = () => teamModalRef.current.open();

  const TeamRow = ({
    image,
    isLink = false,
    outline = 'outline-white',
  }: {
    image?: string;
    isLink?: boolean;
    outline?: string;
  }) => {
    return (
      <div
        className={`h-7 w-7 sm:h-10 sm:w-10 relative rounded sm:rounded-xl -ml-4 outline outline-3 ${outline} ${
          isLink && 'hover:opacity-80'
        }`}
      >
        <Image className="rounded sm:rounded-xl" layout="fill" src={image} />
      </div>
    );
  };

  const FeedbackButton = ({ huddle }: { huddle: Huddle }) => {
    const { user } = useAuth();
    return (
      <button
        className="h-10 w-50 rounded-lg px-4 border border-transparent text-black"
        onClick={() =>
          window.open(
            `https://wehuddleup.typeform.com/to/ivvOVCvD#email=${user?.email}&huddle_id=${huddle.id}&name=${
              user.name
            }&huddle_name=${huddle.request.company.name}&teammates=${huddle.builders.length > 1}&customer=${
              user.role === Role.Customer
            }`,
            '_blank',
          )
        }
      >
        <FeedbackRounded htmlColor="black" />
        <span className="ml-1.5 font-medium ">Feedback</span>
      </button>
    );
  };

  const JoinRoomButton = ({ huddle }: { huddle: Huddle }) => {
    return (
      <button
        className="bg-white rounded-lg font-small flex whitespace-nowrap"
        onClick={() => window.open(huddle?.request?.roomLink)}
      >
        <span className="font-xs text-huddleFontGray underline">Jump to room</span>
        <span className="ml-1 text-huddleFontGray ">
          <ArrowRightAltRounded />
        </span>
      </button>
    );
  };

  const StatusTag = () => {
    switch (huddle.proposal?.status) {
      case ProposalStatus.InReview:
        return (
          <div className="flex flex-row gap-x-3 items-center mb-4">
            <Tag text={'📄 Agreement Ready'} size="md" color={'green'} roundness="full" />
            <span className="text-[#797979] text-sm">Updated {DateTime.fromISO(huddle.updatedAt).toRelative()}</span>
            <span className="text-[#797979] text-sm"></span>
          </div>
        );
      case ProposalStatus.HuddleReserved:
        return (
          <div>
            <div className="flex flex-row gap-x-3 items-center mb-4">
              <div>
                <Tag text={'⏰ Huddle Reserved'} size="md" color={'green'} roundness="full" />
              </div>
              <div>
                <span className="text-[#797979] text-sm">
                  Updated {DateTime.fromISO(huddle.updatedAt).toRelative()}
                </span>
              </div>
              <div className="flex flex-row ml-auto">
                <div className="flex flex-row gap-x-10 items-center">
                  <div className="flex flex-row gap-x-1 items-center">
                    <Timer />
                    <Countdown date={huddle.proposal.expirationDate} renderer={renderer}>
                      <span>Reservation Period has expired</span>
                    </Countdown>
                  </div>
                  <div className="">
                    <PayNowButton huddle={huddle} />
                  </div>
                </div>
              </div>
            </div>
            <div className="">
              <p className="text-[#151515] text-md">
                Now that you’ve reserved your Huddle, your team is being held for you for the next five business days.
                Whenever you’re ready to move forward, hit Pay now. If you have any questions, hop into{' '}
                <a className="text-decoration-line: underline font-bold" href={huddle.request.roomLink}>
                  your Huddle Room
                </a>{' '}
                and ask away.{' '}
              </p>
            </div>
          </div>
        );
      case ProposalStatus.FirstInvoicePaid:
        return (
          <div>
            <div className="flex flex-row gap-x-3 items-center mb-4">
              <Tag text={'🎉 Huddle Paid'} size="md" color={'green'} roundness="full" />
              <span className="text-[#797979] text-sm">Updated {DateTime.fromISO(huddle.updatedAt).toRelative()}</span>
              <span className="text-[#797979] text-sm"></span>
            </div>
            <div className="ml-auto">
              <p className="text-[#151515] text-md">
                It’s on! Your huddle is booked and paid for. Hop back into{' '}
                <a className="text-decoration-line: underline font-bold" href={huddle.request.roomLink}>
                  your Huddle Room
                </a>{' '}
                for next steps. Well officially kick off the engagement on{' '}
                {moment(huddle.reservedStartDate).format('MMMM Do, YYYY')}.
              </p>
            </div>
          </div>
        );

      case ProposalStatus.AgreementExpired:
        return (
          <div className="flex flex-row gap-x-3 items-center mb-4">
            <Tag text={'📄 Agreement Expired'} size="md" color={'green'} roundness="full" />
            <span className="text-[#797979] text-sm">Updated {DateTime.fromISO(huddle.updatedAt).toRelative()}</span>
            <span className="text-[#797979] text-sm"></span>
          </div>
        );
      case ProposalStatus.ReservationExpired:
        return (
          <div className="flex flex-row gap-x-3 items-center mb-4">
            <Tag text={'📄 Reservation Expired'} size="md" color={'green'} roundness="full" />
            <span className="text-[#797979] text-sm">Updated {DateTime.fromISO(huddle.updatedAt).toRelative()}</span>
            <span className="text-[#797979] text-sm"></span>
          </div>
        );
      case ProposalStatus.TeamLive:
        return (
          <div className="">
            <div className="lg: flex flex-row gap-x-3 mb-4">
              <CompanyInfo huddle={huddle} request={huddle?.request} />
              <span className="text-[#797979] text-sm"></span>
            </div>
            <div className="lg:flex flex-row">
              <div className="lg:ml-20">
                <div className="justify-left">
                  <InfoItem title="TEAM" size="sm" value={''} />
                </div>
                <div className="flex flex-row ml-5">
                  {huddle.proposal.selectedOptIns.map(dataObj => {
                    const { id, photoUrl } = dataObj?.user;
                    return <TeamRow key={id} image={photoUrl || DEFAULT_USER_AVATAR} />;
                  })}
                </div>
              </div>
              {huddle?.proposal?.paySchedule && (
                <div className="lg:ml-20 flex-1">
                  <div className="justify-left">
                    <InfoItem
                      title="DATES"
                      valueColor="black"
                      size="sm"
                      value={`${moment(huddle.proposal.paySchedule?.payIntervals[0].startDate)
                        .format('MMM Do')
                        .valueOf()} - ${moment(huddle.proposal.paySchedule?.payIntervals[0].startDate)
                        .add(huddle.proposal.paySchedule?.payIntervals.length, 'M')
                        .format('MMM Do')
                        .valueOf()}`}
                    />
                  </div>
                </div>
              )}
              <div className="lg:flex flex-row items-center gap-x-20">
                <Link href={`/huddles/${huddle?.request?.slug}/proposal/${huddle?.proposal?.id}`} passHref>
                  <a>
                    <button className="rounded-lg">
                      <div className=" text-[#3C3D3E] underline font-medium">View Agreement</div>
                    </button>
                  </a>
                </Link>
                <FeedbackButton huddle={huddle} />
                <JoinRoomButton huddle={huddle} />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const Content = () => {
    return (
      <div className="flex flex-row items-start">
        {[ProposalStatus.InReview, ProposalStatus.AgreementExpired, ProposalStatus.ReservationExpired].includes(
          huddle.proposal?.status,
        ) && (
          <div className="flex flex-row w-full">
            <div className="basis-0.5">
              <Avatar image={huddle.request.partner?.photoUrl || DEFAULT_USER_AVATAR} size="xs" roundness="md" />
            </div>
            <div className="basis-3/5 ml-5">
              <p className="text-[#151515] text-md">
                {huddle.proposal?.teamNote
                  ? '"' + huddle.proposal?.teamNote.substring(0, 200) + '..."'
                  : 'Your huddle agreement is ready for review.'}
              </p>
            </div>
            <div className="basis-1/5 ml-auto">
              <ReviewHuddleAgreementButton huddle={huddle} />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white flex flex-col  p-6 rounded-tl-xl rounded-tr-xl drop-shadow">
      <StatusTag />
      <Content />
    </div>
  );
};

export default HuddleStatusHeader;
